import {
  bySubstance,
  microHeading,
  microHeadingCss,
  pageLabel,
  panel,
  panelCss,
  rankMarker,
  rankMarkerCss,
  readingTime,
} from '../armKit';
import { collapsedRowCss, MUTED } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;
type Head = ProfileCard & { url: string };

const FLAG = 'readingqueue';
const HEAD_CLASS = 'profile-liautotest-queue__head';
const START_CLASS = 'profile-liautotest-queue__start';
const START_TITLE_CLASS = `${START_CLASS}-title`;
const META_CLASS = 'profile-liautotest-queue__meta';
const START_LABEL = 'Start reading';
const START_SOURCE = 'profile-liautotest-queue-start';

// The two blues are the arm's own — the palette holds none — and the border
// override rides on HEAD_CLASS, an arm-own class, rather than on the kit's
// private panel class, since exactly one panel exists in this arm's scope.
function queueCss(scope: string): string {
  return `
${scope} .${HEAD_CLASS} {
  border-color: #0645b1;
  padding: 18px 20px;
}
${scope} .${START_CLASS} {
  display: block;
  background: #0645b1;
  border-radius: 4px;
  padding: 12px 16px;
  color: #fff;
  text-decoration: none;
}
${scope} .${START_CLASS}:hover,
${scope} .${START_CLASS}:focus {
  background: #0057eb;
  color: #fff;
  text-decoration: none;
}
${scope} .${START_TITLE_CLASS} {
  display: block;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
}
${scope} .${META_CLASS} {
  margin: 10px 0 0;
  font-size: 13px;
  color: ${MUTED};
}
`;
}

function span(className: string, text: string): HTMLElement {
  const element = document.createElement('span');
  element.className = className;
  element.textContent = text;

  return element;
}

function metaText(pageCount: number | null | undefined): string {
  return [pageLabel(pageCount), readingTime(pageCount)]
    .filter((part): part is string => part != null)
    .join(' · ');
}

function headPanel(head: Head, facts: Facts, ctx: ArmContext): HTMLElement {
  const { root, body } = panel();
  root.classList.add(HEAD_CLASS);

  const action = document.createElement('a');
  action.className = START_CLASS;
  action.href = head.url;
  action.appendChild(microHeading(START_LABEL, 'span'));
  action.appendChild(span(START_TITLE_CLASS, head.title));
  action.addEventListener('click', () => {
    ctx.track(START_SOURCE, { entityId: head.workId });
  });
  body.appendChild(action);

  const meta = metaText(facts.get(head.workId)?.pageCount);
  if (meta !== '') {
    const line = document.createElement('p');
    line.className = META_CLASS;
    line.textContent = meta;
    body.appendChild(line);
  }

  return root;
}

// Counted off the cards' own flags rather than held in module state, so an
// appended batch continues the numbering without the arm outliving its
// pageview.
function enqueue(cards: ProfileCard[], facts: Facts, ctx: ArmContext): void {
  let position = ctx.cards().filter((card) => card.flag(FLAG) != null).length;

  cards.forEach((card) => {
    if (card.flag(FLAG) != null) return;
    card.setFlag(FLAG);
    position += 1;

    card.collapse();

    const pages = pageLabel(facts.get(card.workId)?.pageCount);
    if (pages != null) card.addChip(pages);

    card.prependToTitle(rankMarker(`${position}.`));
  });
}

export const readingQueue: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0) return;

    const rank = bySubstance((workId) => facts.get(workId)?.pageCount);
    // Ranked on a copy first: the head has to be known before the slot is
    // mounted, because a corpus with no linkable work degrades to control and
    // must leave no container behind.
    const head = [...ctx.cards()]
      .sort(rank)
      .find((card): card is Head => card.url != null);
    if (head == null) return;

    const slot = ctx.mountSlot('listHero');
    if (slot == null) return;

    ctx.injectStyles(
      panelCss(ctx.scope) +
        queueCss(ctx.scope) +
        microHeadingCss(ctx.scope, { margin: '0 0 3px', color: null }) +
        rankMarkerCss(ctx.scope, { fontSize: null, marginRight: '4px' }) +
        collapsedRowCss(ctx.scope, { padding: '10px 0' }),
    );
    ctx.reorderCards(rank, 'list');

    slot.appendChild(headPanel(head, facts, ctx));
    enqueue(ctx.cards(), facts, ctx);
  },

  // No re-rank and no second head: a batch joins the tail of the queue in the
  // order it arrived. Gated on the head onReady mounted rather than on a fresh
  // read, so a late batch cannot engage an arm that degraded to control.
  onCards: async (cards, ctx) => {
    if (document.querySelector(`.${HEAD_CLASS}`) == null) return;

    enqueue(cards, await ctx.workFacts(), ctx);
  },
};
